// AppointmentCalendarGrid.jsx — VERSION CORRIGÉE & FIABLE
import React, { useMemo } from 'react';
import {
  Box,
  Typography,
  Chip,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  Person as PersonIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import moment from 'moment';
import 'moment/locale/fr';
import { theme } from '../../../theme';

moment.locale('fr');

const AppointmentCalendarGrid = ({ appointments = [], startHour = 8, endHour = 20 }) => {
  const START_HOUR = startHour;
  const END_HOUR = endHour;
  const HOUR_HEIGHT = 60;
  const hours = Array.from({ length: END_HOUR - START_HOUR + 1 }, (_, i) => i + START_HOUR);

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'confirmé': case 'confirmed': return theme.colors.success[500];
      case 'en attente': case 'pending': return theme.colors.warning[500];
      case 'annulé': case 'cancelled': return theme.colors.error[500];
      case 'terminé': case 'completed': return theme.colors.info[500];
      default: return theme.colors.primary[500];
    }
  };

  const getStatusLabel = (status) => {
    switch (status?.toLowerCase()) {
      case 'confirmé': case 'confirmed': return 'Confirmé';
      case 'en attente': case 'pending': return 'En attente';
      case 'annulé': case 'cancelled': return 'Annulé';
      case 'terminé': case 'completed': return 'Terminé';
      default: return status || 'Inconnu';
    }
  };

  // TOOLTIP
  const AppointmentTooltip = ({ appointment }) => {
    const start = moment(appointment.start_time_iso || appointment.time || appointment.start_time);
    const end = moment(appointment.end_time || appointment.end_time_iso || start.clone().add(60, 'minutes'));
    const duration = end.diff(start, 'minutes');

    return (
      <Box sx={{ p: 1.5, minWidth: 240 }}>
        <Typography sx={{ fontWeight: 700, color: '#fff', fontSize: '1rem', mb: 1, display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <PersonIcon sx={{ fontSize: 18 }} />
          {appointment.client_name || appointment.client}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 4, height: 4, bgcolor: '#fff', borderRadius: '50%' }} />
            <Typography sx={{ color: '#e0e0e0', fontSize: '0.9rem' }}>
              <strong>Service :</strong> {appointment.service_name || appointment.service}
            </Typography>
          </Box>
          {appointment.team_member_name && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <PersonIcon sx={{ fontSize: 14, color: '#90caf9' }} />
              <Typography sx={{ color: '#e0e0e0', fontSize: '0.9rem' }}>
                <strong>Employé :</strong> {appointment.team_member_name}
              </Typography>
            </Box>
          )}
          {appointment.salon_name && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <BusinessIcon sx={{ fontSize: 14, color: '#a5d6a7' }} />
              <Typography sx={{ color: '#e0e0e0', fontSize: '0.9rem' }}>
                <strong>Salon :</strong> {appointment.salon_name}
              </Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{
              width: 28,
              height: 28,
              bgcolor: '#fff',
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#333',
              fontWeight: 700,
              fontSize: '0.7rem',
            }}>
              {start.format('HH')}
            </Box>
            <Typography sx={{ color: '#fff', fontSize: '0.9rem', fontWeight: 500 }}>
              {start.format('HH:mm')} to {end.format('HH:mm')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip label={`${duration} min`} size="small" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 600, height: 22, fontSize: '0.75rem' }} />
            <Typography sx={{ color: '#e0e0e0', fontSize: '0.9rem' }}><strong>Durée</strong></Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 0.5 }}>
            <Box sx={{ px: 1.5, py: 0.5, borderRadius: 1, bgcolor: getStatusColor(appointment.status), color: '#fff', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase' }}>
              {getStatusLabel(appointment.status)}
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  // POSITIONNEMENT CORRIGÉ (force HH:mm)
  const positionedAppointments = useMemo(() => {
    return (appointments || [])
      .map((a) => {
        // EXTRAIRE UNIQUEMENT L'HEURE au format HH:mm
        const timeStr = (
          a.start_time_iso ||
          a.time ||
          a.start_time ||
          ''
        ).match(/\d{2}:\d{2}/)?.[0];

        if (!timeStr) return null;

        const [hours, minutes] = timeStr.split(':').map(Number);
        const start = moment().hour(hours).minute(minutes).second(0);

        const endStr = (
          a.end_time ||
          a.end_time_iso ||
          ''
        ).match(/\d{2}:\d{2}/)?.[0];

        const end = endStr
          ? moment().hour(endStr.split(':')[0]).minute(endStr.split(':')[1])
          : start.clone().add(a.service_duration || 60, 'minutes');

        if (!start.isValid() || !end.isValid()) return null;

        const duration = end.diff(start, 'minutes');
        const top = ((hours - START_HOUR) + minutes / 60) * HOUR_HEIGHT;
        const height = Math.max((duration / 60) * HOUR_HEIGHT, 30);

        return {
          ...a,
          start_time: start,
          end_time: end,
          topPosition: top,
          height,
          durationMinutes: duration,
          client_name: a.client_name || a.client || 'Inconnu',
          service_name: a.service_name || a.service || 'Service',
          status: a.status || 'confirmed',
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.topPosition - b.topPosition);
  }, [appointments, START_HOUR, HOUR_HEIGHT]);

  return (
    <Box sx={{ position: 'relative', minHeight: `${hours.length * HOUR_HEIGHT}px`, bgcolor: '#fafafa' }}>

      {/* LIGNES HEURES */}
      {hours.map((hour, idx) => (
        <Box
          key={hour}
          sx={{
            position: 'absolute',
            top: `${idx * HOUR_HEIGHT}px`,
            left: 0,
            right: 0,
            height: HOUR_HEIGHT,
            borderTop: `1px solid ${theme.colors.primary[200]}`,
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />
      ))}

      {/* DEMI-HEURES */}
      {hours.slice(0, -1).map((hour, idx) => (
        <Box
          key={`half-${hour}`}
          sx={{
            position: 'absolute',
            top: `${idx * HOUR_HEIGHT + 29}px`,
            left: 0,
            right: 0,
            borderTop: `1px dashed ${theme.colors.primary[100]}`,
            pointerEvents: 'none',
            zIndex: 1,
          }}
        />
      ))}

      {/* COLONNE HEURES (FIXE) */}
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: 80,
          backgroundColor: '#fff',
          zIndex: 5,
          borderRight: `1px solid ${theme.colors.primary[200]}`,
        }}
      >
        {hours.map((hour, idx) => (
          <Box
            key={hour}
            sx={{
              position: 'absolute',
              top: `${idx * HOUR_HEIGHT}px`,
              width: '100%',
              height: HOUR_HEIGHT,
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'center',
              pt: 0.5,
            }}
          >
            <Typography sx={{ fontSize: '0.75rem', color: theme.colors.text.secondary, fontWeight: 500 }}>
              {`${hour}:00`}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* ZONE RDV */}
      <Box sx={{ marginLeft: '80px', position: 'relative', pr: 2 }}>
        {positionedAppointments.map((a) => (
          <Tooltip
            key={a.id || a.appointment_id || Math.random()}
            title={<AppointmentTooltip appointment={a} />}
            placement="top"
            componentsProps={{ tooltip: { sx: { bgcolor: '#1a1a1a', color: '#fff', borderRadius: 2, p: 0 } } }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: `${a.topPosition}px`,
                left: 16,
                right: 16,
                height: `${a.height}px`,
                minHeight: 30,
                backgroundColor: getStatusColor(a.status),
                border: `1px solid ${getStatusColor(a.status)}`,
                borderLeft: `4px solid ${getStatusColor(a.status)}`,
                borderRadius: 1,
                cursor: 'pointer',
                overflow: 'hidden',
                boxSizing: 'border-box',
                zIndex: 2,
                '&:hover': { boxShadow: 3, zIndex: 10, '& .actions': { opacity: 1 } },
              }}
            >
              <Box
                sx={{
                  backgroundColor: 'rgba(255,255,255,0.95)',
                  borderRadius: 0.5,
                  p: 1,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: a.durationMinutes <= 30 ? 'center' : 'flex-start',
                }}
              >
                {a.durationMinutes <= 30 ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', color: theme.colors.text.primary, flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {a.client_name}
                    </Typography>
                    <Typography sx={{ fontSize: '0.7rem', color: theme.colors.text.secondary }}>
                      {a.start_time.format('HH:mm')} • {a.durationMinutes} min
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: theme.colors.text.primary, flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {a.client_name}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        <Typography sx={{ fontSize: '0.75rem', color: theme.colors.text.secondary }}>
                          {a.start_time.format('HH:mm')} - {a.end_time.format('HH:mm')}
                        </Typography>
                        <Chip label={`${a.durationMinutes} min`} size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: theme.colors.grey[100], color: theme.colors.text.secondary }} />
                        <Chip label={getStatusLabel(a.status)} size="small" sx={{ height: 18, fontSize: '0.65rem', bgcolor: getStatusColor(a.status), color: 'white' }} />
                      </Box>
                    </Box>

                    {a.height > 50 && (
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Typography sx={{ fontSize: '0.8rem', color: theme.colors.text.secondary }}>
                          {a.service_name}
                        </Typography>
                        {a.team_member_name && (
                          <Chip icon={<PersonIcon sx={{ fontSize: 10 }} />} label={a.team_member_name} size="small" sx={{ height: 20, fontSize: '0.7rem', bgcolor: theme.colors.primary[100], color: theme.colors.primary[700] }} />
                        )}
                        {a.salon_name && (
                          <Chip icon={<BusinessIcon sx={{ fontSize: 10 }} />} label={a.salon_name} size="small" sx={{ height: 20, fontSize: '0.7rem', bgcolor: theme.colors.secondary[100], color: theme.colors.secondary[700] }} />
                        )}
                        {a.client_phone && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PhoneIcon sx={{ fontSize: 14, color: theme.colors.text.secondary }} />
                            <Typography sx={{ fontSize: '0.75rem', color: theme.colors.text.secondary }}>
                              {a.client_phone}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}
                  </>
                )}

                <Box className="actions" sx={{ position: 'absolute', top: 6, right: 6, display: 'flex', gap: 0.3, opacity: 0, transition: 'opacity 0.2s' }}>
                  <IconButton size="small" sx={{ p: '2px', bgcolor: 'rgba(255,255,255,0.95)', width: 20, height: 20, color: theme.colors.primary[600] }}>
                    <VisibilityIcon sx={{ fontSize: 13 }} />
                  </IconButton>
                  <IconButton size="small" sx={{ p: '2px', bgcolor: 'rgba(255,255,255,0.95)', width: 20, height: 20, color: theme.colors.warning[600] }}>
                    <EditIcon sx={{ fontSize: 13 }} />
                  </IconButton>
                  <IconButton size="small" sx={{ p: '2px', bgcolor: 'rgba(255,255,255,0.95)', width: 20, height: 20, color: theme.colors.error[600] }}>
                    <DeleteIcon sx={{ fontSize: 13 }} />
                  </IconButton>
                </Box>
              </Box>
            </Box>
          </Tooltip>
        ))}
      </Box>
    </Box>
  );
};

export default AppointmentCalendarGrid;